import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { aideAPI } from '../../services/api';

/**
 * AIDE CONTEXTUELLE — le contexte commun à tous les modules (étape A1)
 * ====================================================================
 * Protocole : doc/specs_techniques/aide_contextuelle.md (§ 3 : la clé d'écran)
 *
 * ── LA CLÉ DE L'ÉCRAN OUVERT ───────────────────────────────────────────────
 * Le module connaît ses onglets de premier niveau (`base`, ex. « tuteurs ») ; les écrans qui ont leurs PROPRES
 * sous-onglets (Paramétrage, Tuteurs…) les connaissent seuls. Chacun ANNONCE donc sa clé avec `useEcranAide` ;
 * la plus longue annonce qui prolonge `base` l'emporte (« tuteurs/param/cfa » sur « tuteurs/param »). Un écran
 * démonté retire son annonce : on ne garde jamais la clé d'un écran quitté.
 *
 * ── ALLER À UN ÉCRAN DEPUIS L'AIDE ─────────────────────────────────────────
 * `ouvrir(cle)` passe au module (onglets de premier niveau) ET pose une DEMANDE que les écrans à sous-onglets
 * lisent avec `useDemandeEcran`. La demande n'est consommée que par le dernier niveau (sinon le sous-onglet
 * suivant, monté juste après, ne la verrait plus).
 *
 * ── ÉTAPE A3 : CE QUE LE COMPTE A DÉJÀ VU (v76) ─────────────────────────────
 * Les LECTURES du compte (bulle « Nouveau » vue, dernier « Quoi de neuf » lu) viennent du serveur — par compte,
 * pas par poste. Tant qu'elles ne sont pas lues, ni bulle ni pastille : on n'affiche pas un « nouveau » douteux.
 *
 * ⚠️ L'aide ne touche à aucune règle métier : elle lit l'onglet ouvert ; elle n'écrit que les lectures du compte
 * et les signalements.
 */

const Contexte = createContext(null);
/**
 * ⚠️ Le contexte des ÉCRANS du module, séparé et STABLE (2026-10-13) : un écran qui annonce sa clé ne doit pas se
 * redessiner à chaque changement de l'aide (écran courant, volet, lectures…). Avant, il lisait tout le contexte : le
 * redessin interrompait un défilement doux lancé au même instant (banc 35a rouge une fois sur deux).
 */
const ContexteEcran = createContext(null);

/**
 * `accessible(cle)` (facultatif, Planning oraux 2026-10-17) : null si l'écran peut s'ouvrir d'ici, sinon la RAISON
 * (« ouvrez d'abord un planning ») ; les liens de l'aide vers cet écran restent alors visibles, non cliquables, raison dite.
 */
export function AideProvider({ donnees, base, departementId, onOuvrir, ouvrirSur, accessible, children }) {
    const [annonces, setAnnonces] = useState({});
    const [ouvert, setOuvert] = useState(false);
    // Étape A3 : l'onglet du volet (Cet onglet · Je veux… · Quoi de neuf) et la fenêtre « Proposer une amélioration »
    // (v99) — `signalerOuvert` : false, true (nature « idée » par défaut) ou 'aide' (ouverte depuis « Aucune tâche… »).
    const [vue, setVue] = useState('onglet');
    const [signalerOuvert, setSignalerOuvert] = useState(false);
    const [lectures, setLectures] = useState(null);       // { bulle_vue, neuf_lu_jusqu_au } — null tant que non lues
    // Étape A2 : la grande fenêtre « Comprendre le processus métier ».
    const [processusOuvert, setProcessusOuvert] = useState(false);
    const [demande, setDemande] = useState(null);           // { cle, n }

    const annoncer = useCallback((id, cle) => {
        setAnnonces((a) => {
            if (cle == null) {
                if (!(id in a)) return a;
                const reste = { ...a };
                delete reste[id];
                return reste;
            }
            return a[id] === cle ? a : { ...a, [id]: cle };
        });
    }, []);

    const ecran = useMemo(() => {
        let meilleure = base;
        Object.values(annonces).forEach((c) => {
            if ((c === base || c.startsWith(base + '/')) && c.length > meilleure.length) meilleure = c;
        });
        return meilleure;
    }, [annonces, base]);

    // Le rappel du module passe par une référence : le changer ne doit rien relancer.
    const rappel = useRef(onOuvrir);
    useEffect(() => { rappel.current = onOuvrir; }, [onOuvrir]);
    const raison = useCallback((cle) => (accessible ? accessible(cle) : null), [accessible]);
    const ouvrir = useCallback((cle) => {
        if (accessible && accessible(cle)) return;
        setDemande({ cle, n: Date.now() });
        if (rappel.current) rappel.current(cle);
    }, [accessible]);
    const consommer = useCallback((n) => setDemande((d) => (d && d.n === n ? null : d)), []);
    // Arrivée depuis Administration → Aide : l'écran signalé, l'aide ouverte sur « Cet onglet ».
    useEffect(() => {
        if (!ouvrirSur) return;
        ouvrir(ouvrirSur);
        setVue('onglet');
        setOuvert(true);
    }, [ouvrirSur, ouvrir]);

    // Les lectures du compte pour ce module.
    const module = donnees.module;
    useEffect(() => {
        let vivant = true;
        aideAPI.lectures(module)
            .then((r) => { if (vivant) setLectures(r.data); })
            // Illisibles : on n'importune pas (pas de bulle), la pastille compte tout comme non lu.
            .catch(() => { if (vivant) setLectures({ bulle_vue: true, neuf_lu_jusqu_au: null }); });
        return () => { vivant = false; };
    }, [module]);
    const marquerBulleVue = useCallback(() => {
        setLectures((l) => ({ ...(l || {}), bulle_vue: true }));
        aideAPI.ecrireLectures(module, { bulle_vue: true }).catch(() => { /* revue au prochain chargement : sans gravité */ });
    }, [module]);
    const neuf = donnees.NEUF || [];
    const plusRecent = neuf.reduce((m, x) => (x.date > m ? x.date : m), '');
    const nonLus = lectures ? neuf.filter((x) => x.date > (lectures.neuf_lu_jusqu_au || '')).length : 0;
    const marquerNeufLu = useCallback(() => {
        if (!plusRecent) return;
        setLectures((l) => ({ ...(l || {}), neuf_lu_jusqu_au: plusRecent }));
        aideAPI.ecrireLectures(module, { neuf_lu_jusqu_au: plusRecent }).catch(() => { /* idem */ });
    }, [module, plusRecent]);
    const ouvrirVolet = useCallback((v) => { setVue(v || 'onglet'); setOuvert(true); }, []);

    // F1 ouvre « Comprendre cet onglet » ; Échap ferme, dans l'ordre : Signaler, la fenêtre du processus, le volet.
    const fenetreOuverte = useRef(false);
    const signalerEnCours = useRef(false);
    useEffect(() => { fenetreOuverte.current = processusOuvert; }, [processusOuvert]);
    useEffect(() => { signalerEnCours.current = signalerOuvert; }, [signalerOuvert]);
    useEffect(() => {
        const touche = (e) => {
            if (e.key === 'F1') { e.preventDefault(); setVue('onglet'); setOuvert(true); }
            else if (e.key === 'Escape') {
                if (signalerEnCours.current) setSignalerOuvert(false);
                else if (fenetreOuverte.current) setProcessusOuvert(false);
                else setOuvert(false);
            }
        };
        window.addEventListener('keydown', touche);
        return () => window.removeEventListener('keydown', touche);
    }, []);

    const valeur = useMemo(() => ({
        donnees, ecran, departementId, ouvert, setOuvert, ouvrirVolet, vue, setVue, processusOuvert, setProcessusOuvert,
        signalerOuvert, setSignalerOuvert, lectures, nonLus, marquerBulleVue, marquerNeufLu, annoncer, ouvrir, raison, demande, consommer
    }), [donnees, ecran, departementId, ouvert, ouvrirVolet, vue, processusOuvert, signalerOuvert, lectures, nonLus,
        marquerBulleVue, marquerNeufLu, annoncer, ouvrir, raison, demande, consommer]);
    const valeurEcran = useMemo(() => ({ annoncer, demande, consommer }), [annoncer, demande, consommer]);
    return (
        <Contexte.Provider value={valeur}>
            <ContexteEcran.Provider value={valeurEcran}>{children}</ContexteEcran.Provider>
        </Contexte.Provider>
    );
}

export const useAide = () => useContext(Contexte);

let compteur = 0;

/** Un écran annonce sa clé d'aide (ex. « tuteurs/param/cfa »). Sans module aidé autour, ne fait rien. */
export function useEcranAide(cle) {
    const ctx = useContext(ContexteEcran);
    const annoncer = ctx ? ctx.annoncer : null;
    const id = useRef(null);
    if (id.current === null) { compteur += 1; id.current = 'e' + compteur; }
    useEffect(() => {
        if (!annoncer || !cle) return undefined;
        const moi = id.current;
        annoncer(moi, cle);
        return () => annoncer(moi, null);
    }, [annoncer, cle]);
}

/**
 * Un écran à sous-onglets suit les demandes de l'aide : si la demande commence par `prefixe/`, `appliquer` reçoit
 * le segment suivant (ex. prefixe « tuteurs », demande « tuteurs/param/cfa » → « param »).
 */
export function useDemandeEcran(prefixe, appliquer) {
    const ctx = useContext(ContexteEcran);
    const demande = ctx ? ctx.demande : null;
    const consommer = ctx ? ctx.consommer : null;
    const rappel = useRef(appliquer);
    useEffect(() => { rappel.current = appliquer; }, [appliquer]);
    useEffect(() => {
        if (!demande || !demande.cle.startsWith(prefixe + '/')) return;
        const segments = demande.cle.slice(prefixe.length + 1).split('/');
        rappel.current(segments[0]);
        if (segments.length === 1 && consommer) consommer(demande.n);
    }, [demande, prefixe, consommer]);
}
